import { CurrencyType, DescendantResource, Id, ImageResource, LanguageResource, TranslationEntry } from "@dgs/core";
import { AddressIndexResource } from "./address";
import { ApplicationDetailResource } from "./application";
import { CostCategoryOptionResource } from "./costCategory";
import { CountryOptionResource } from "./country";
import { Currency } from "./currency";
import { EventCategoryOptionResource } from "./eventCategory";
import { EventFormat } from "./eventFormat";
import { EventTypeOptionResource } from "./eventType";
import { IndustryOptionResource } from "./industry";
import { InvitationListStats } from "./invitationList";
import { LocationOptionResource } from "./location";
import { OrganizationalUnitOptionResource } from "./organizationalUnit";
import { OrganizerOptionResource } from "./organizer";
import { PreferenceOptionResource } from "./preference";
import { RegionOptionResource } from "./region";
import { TagOptionResource } from "./tag";
import { TargetGroupOptionResource } from "./targetGroup";
import { UserOptionResource } from "./user";

export type EventParticipationStatus = "none" | "withParticipation" | "noParticipation";

export type EventVisibility = "public" | "logged_in_users" | "my_project_team";

export const visibilityOptions: EventVisibility[] = ["public", "logged_in_users", "my_project_team"];

export interface EventIndexResource {
	id: Id;
	name: string;
	start: string;
	end: string;
	timezone: string;
	isHighlight: boolean;
	visibility: EventVisibility;
	address: AddressIndexResource | null;
	projectManagers: UserOptionResource[];
	hasProjectManagerPermissions: boolean;
	hasProjectMemberPermissions: boolean;
	location: LocationOptionResource | null;
	participationStatus: EventParticipationStatus;
}

export interface EventListOverviewResource {
	id: Id;
	name: string;
	start: string;
	timezone: string;
	end: string;
	eventType: EventTypeOptionResource;
	image: ImageResource;
	region: RegionOptionResource | null;
	projectManagers: UserOptionResource[];
	isHighlight: boolean;
	participationStatus: EventParticipationStatus;
	address: AddressIndexResource | null;
	rating: number | null;
}

export interface EventTenantsPlannedPersonCount {
	tenant: ITenantOptionResource;
	plannedPersonCount: number;
}

export interface EventUpdateResource extends Omit<EventFormValues, "tenantPlannedPersonCounts"> {
	id: Id;
	tenantPlannedPersonCounts: EventTenantsPlannedPersonCount[];
}

interface EventTenantsPlannedPersonCountRequest {
	tenantId: Id;
	plannedPersonCount: number;
}

export interface EventFormTranslationValues {
	name: string;
	additionalInfo: DescendantResource[] | null;
}

export interface BaseEventFormValues {
	eventType: EventTypeOptionResource | null;
	translations: TranslationEntry<EventFormTranslationValues>[];
}

export type ITenantOptionResource = {
	id: Id;
	name: string;
};

export interface EventTenantsPlannedPersonCountFormState {
	tenant: ITenantOptionResource | null;
	plannedPersonCount: number;
}

interface EventTenantsPlannedPersonCountRequest {
	tenantId: Id;
	plannedPersonCount: number;
}

// Step 1
export interface EventBaseDataFormValues extends BaseEventFormValues {
	eventType: EventTypeOptionResource | null;
	start: string | null;
	end: string | null;
	timezone: string;
	primaryLanguage: LanguageResource | null;
	languages: LanguageResource[];
	organizer: OrganizerOptionResource | null;
	visibility: EventVisibility;
	image: ImageResource | null;
	location: LocationOptionResource | null;
}

// Step 1.5
export interface EventAddress {
	street: string;
	houseNumber: string;
	zip: string;
	city: string;
	country: CountryOptionResource | null;
	additionalInfo: string;
}

export interface EventAddressFormValues extends BaseEventFormValues {
	address: EventAddress;
}

type UserResponsibilityId = Id;
// Step 2
export interface EventUsersFormValues extends BaseEventFormValues {
	eventUsers: Record<UserResponsibilityId, UserOptionResource[]>;
}

// Step 3
export interface EventParticipationFormValues extends BaseEventFormValues {
	isInviteOnly: "true" | "false" | null;
	link: string;
	tenantPlannedPersonCounts: EventTenantsPlannedPersonCountFormState[];
}

// Step 4
export interface EventCostsFormValues extends BaseEventFormValues {
	currency: Currency | null;
	eventCostCategories: EventCostCategoryResource[];
}

export interface EventDetailsFormValues extends BaseEventFormValues {
	format: EventFormat | null;
	eventCategory: EventCategoryOptionResource | null;
	industries: IndustryOptionResource[];
	targetGroup: TargetGroupOptionResource | null;
	region: RegionOptionResource | null;
	primaryOrganizationalUnit: OrganizationalUnitOptionResource | null;
	organizationalUnits: OrganizationalUnitOptionResource[];
	tags: TagOptionResource[];
	preferences: PreferenceOptionResource[];
	isHighlight: boolean;
	eventGoal: string | null;
}

export type EventFormValues = EventBaseDataFormValues &
	EventAddressFormValues &
	EventUsersFormValues &
	EventParticipationFormValues &
	EventCostsFormValues &
	EventDetailsFormValues;

export interface EventUserSaveRequest {
	userResponsibilityId: Id;
	userIds: Id[];
}

export interface EventSaveRequest {
	translations: TranslationEntry<EventFormTranslationValues>[];
	// Step 1
	eventTypeId: Id;
	start: string;
	end: string;
	timezone: string;
	primaryLanguageIsoCode: Id;
	languageIsoCodes: Id[];
	organizerId: Id | null;
	visibility: EventVisibility;
	imageId: Id | null;
	locationId: Id | null;
	//Step 1.5
	address: {
		street: string;
		houseNumber: string;
		zip: string;
		city: string;
		countryId: Id;
		additionalInfo: string;
	} | null;
	//Step 2
	eventUsers: EventUserSaveRequest[];
	// Step 3
	isInviteOnly: boolean | null;
	link: string | null;
	tenantPlannedPersonCounts: EventTenantsPlannedPersonCountRequest[];
	// Step 4
	currencyId: Id | null;
	eventCostCategories: EventCostCategorySaveRequest[];
	// Step 5
	format: EventFormat | null;
	eventCategoryId: Id | null;
	industryIds: Id[];
	targetGroupId: Id | null;
	regionId: Id | null;
	primaryOrganizationalUnitId: Id | null;
	organizationalUnitIds: Id[];
	tagIds: Id[];
	preferenceIds: Id[];
	isHighlight: boolean;
	eventGoal: string | null;
}

export interface SimilarEventRequest {
	name: string;
	location: string | null;
	start: string;
	end: string;
	eventId: Id | null;
}

export interface EventCostCategoryResource {
	costCategory: CostCategoryOptionResource;
	plannedCost: number | null;
	actualCost: number | null;
	commentAllowed: boolean;
	comment: string | null;
}

export interface EventCostCategorySaveRequest {
	costCategoryId: Id;
	plannedCost: number | null;
	actualCost: number | null;
	comment: string | null;
}

export interface EventCostCategoryIndexResource {
	id: Id;
	name: string;
	plannedCost: number | null;
	actualCost: number | null;
	currency: CurrencyType | null;
}

export interface EventDetailResource {
	id: Id;
	name: string;
	eventType: EventTypeOptionResource;
	start: string;
	end: string;
	timezone: string;
	organizer: OrganizerOptionResource | null;
	visibility: EventVisibility;
	clippingCount: number | null;
	image: ImageResource;
	languages: LanguageResource[];
	location: LocationOptionResource | null;
	plannedPersonCount: number | null;
	invitationListStats: InvitationListStats;
	currency: CurrencyType | null;
	format: EventFormat | null;
	eventCategory: EventCategoryOptionResource | null;
	industries: IndustryOptionResource[];
	targetGroup: TargetGroupOptionResource | null;
	region: RegionOptionResource | null;
	primaryOrganizationalUnit: OrganizationalUnitOptionResource | null;
	organizationalUnits: OrganizationalUnitOptionResource[];
	isHighlight: boolean;
	primaryLanguage: LanguageResource | null;
	tags: TagOptionResource[];
	preferences: PreferenceOptionResource[];
	address: AddressIndexResource | null;
	additionalInfo: DescendantResource[] | null;
	applications: ApplicationDetailResource[];
	isInviteOnly: boolean | null;
	link: string | null;
	creator: UserOptionResource | null;
	hasProjectManagerPermissions: boolean;
	hasProjectMemberPermissions: boolean;
	eventGoal: string | null;
	totalPlannedCosts: number | null;
	totalActualCosts: number | null;
	individualCosts: number | null;
	costCategories: EventCostCategoryResource[];
	visitorsMetric: number | null;
	invitedPersonsMetric: number | null;
	registrationsMetric: number | null;
	customersMetric: number | null;
	employeesMetric: number | null;
	eventReachMetric: number | null;
	eventGoalRatingMetric: number | null;
	hasFinishedOffboarding: boolean;
}

export interface EventOffboardingFormValues {
	visitorsMetric: number | null;
	invitedPersonsMetric: number | null;
	registrationsMetric: number | null;
	customersMetric: number | null;
	employeesMetric: number | null;
	eventReachMetric: number | null;
	eventGoalRatingMetric: number | null;
	costCategories: EventCostCategoryFormValues[];
}

export interface EventOffboardingSaveRequest extends Omit<EventOffboardingFormValues, "costCategories"> {
	eventTypeId: Id;
	costCategories: EventCostCategorySaveRequest[];
}

export interface EventCostCategoryFormValues {
	costCategory: CostCategoryOptionResource;
	plannedCost: number | null;
	actualCost: number | null;
	comment: string | null;
}
